import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, X } from "lucide-react";
import api from "@/lib/api";
import { ProofGrid, ProofUploader } from "./JobProof";

export function CompletionForm({ bookingId, tracker, geo, onDone, onClose }) {
  const [req, setReq] = useState(null);
  const [notes, setNotes] = useState("");
  const [checks, setChecks] = useState({});
  const [materials, setMaterials] = useState("");
  const [issues, setIssues] = useState("None");
  const [followUp, setFollowUp] = useState(false);
  const [warranty, setWarranty] = useState("");
  const [declared, setDeclared] = useState(false);
  const [busy, setBusy] = useState(false);
  const after = (tracker?.proofs || []).filter((p) => p.phase === "after");

  const load = async () => { try { const { data } = await api.get(`/jobs/${bookingId}/completion-requirements`); setReq(data); } catch (_) { /* ignore */ } };
  useEffect(() => { load(); }, [bookingId, tracker?.proofs?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const rules = req?.rules || tracker?.rules?.proof || {};
  const checklist = req?.checklist || tracker?.checklist;
  const minAfter = Number(rules.min_after || 1);
  const afterOk = rules.after !== "required" || after.length >= minAfter;

  const submit = async () => {
    if (!declared) return toast.error("Please confirm the completion declaration");
    setBusy(true);
    try {
      await api.post(`/jobs/${bookingId}/complete`, {
        completion_notes: notes, checklist: checks, work_performed: Object.keys(checks).filter((k) => checks[k]), materials_used: materials || null,
        outstanding_issues: issues, follow_up_required: followUp, warranty_info: warranty || null, declaration_confirmed: true, ...(geo?.pos || {}), device: geo?.device,
      });
      toast.success("Completed work submitted — customer notified");
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.response?.data?.error || "Could not submit");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" data-testid="completion-form">
      <div className="bg-white text-[#071b22] w-full sm:max-w-2xl sm:rounded-3xl rounded-t-3xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#165a71]">Project completion</div>
            <h2 className="font-display text-lg font-semibold text-[#071b22]">Submit completed work</h2>
          </div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-slate-100" aria-label="Close" data-testid="completion-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          <section>
            <Label req={rules.after === "required"}>After photos {rules.after === "required" && `(min ${minAfter})`}</Label>
            <ProofGrid proofs={after} emptyText="Upload one or more images showing the completed work." />
            <div className="mt-2"><ProofUploader bookingId={bookingId} phase="after" geo={geo} onAdded={onDone ? () => onDone("refresh") : undefined} label="Add after photo / video" /></div>
            {!afterOk && <div className="text-xs text-rose-600 mt-1" data-testid="after-missing">Required: {minAfter - after.length} more after photo(s).</div>}
          </section>
          {checklist && (
            <section data-testid="completion-checklist">
              <Label req={rules.checklist === "required"}>Work performed — {checklist.name}</Label>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {checklist.items.map((item) => (
                  <label key={item} className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm cursor-pointer ${checks[item] ? "border-emerald-300 bg-emerald-50" : "border-slate-200"}`}>
                    <input type="checkbox" checked={!!checks[item]} onChange={(e) => setChecks({ ...checks, [item]: e.target.checked })} data-testid={`checklist-item-${item.replace(/\s+/g, "-").toLowerCase()}`} /> {item}
                  </label>
                ))}
              </div>
            </section>
          )}
          <section>
            <Label req={rules.notes === "required"}>Completion notes</Label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Explain what was completed…" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="completion-notes" />
          </section>
          <div className="grid sm:grid-cols-2 gap-4">
            <section>
              <Label>Materials used (optional)</Label>
              <textarea value={materials} onChange={(e) => setMaterials(e.target.value)} rows={2} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="completion-materials" />
            </section>
            <section>
              <Label>Outstanding issues</Label>
              <select value={issues} onChange={(e) => setIssues(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="completion-issues">
                {["None", "Additional work recommended", "Customer declined additional work", "Follow-up required"].map((o) => <option key={o}>{o}</option>)}
              </select>
              <label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={followUp} onChange={(e) => setFollowUp(e.target.checked)} data-testid="completion-followup" /> Follow-up required</label>
            </section>
          </div>
          <section>
            <Label>Warranty information (when applicable)</Label>
            <input value={warranty} onChange={(e) => setWarranty(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="completion-warranty" />
          </section>
          <label className="flex items-start gap-3 rounded-2xl border border-[#165a71]/30 bg-[#165a71]/5 p-3 text-sm cursor-pointer" data-testid="completion-declaration">
            <input type="checkbox" checked={declared} onChange={(e) => setDeclared(e.target.checked)} className="mt-1" />
            <span>I confirm that the work described above has been completed and the uploaded images accurately represent the condition of the project at completion.</span>
          </label>
          <button onClick={submit} disabled={busy || !afterOk} className="w-full rounded-full bg-emerald-600 disabled:bg-slate-300 text-white font-semibold py-3.5 text-base inline-flex items-center justify-center gap-2" data-testid="submit-completed-work">
            <CheckCircle2 className="h-5 w-5" /> {busy ? "Submitting…" : "Submit Completed Work"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Label = ({ children, req }) => (
  <div className="text-xs font-bold uppercase tracking-[0.16em] text-[#252A34]/70 mb-2">{children}{req && <span className="ml-1 text-rose-600">· required</span>}</div>
);
